import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CheckCircle, XCircle, Edit3 } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SyndicationResponse() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustPct, setAdjustPct] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/syndication-offers/public/${token}`); setData(r.data); setAdjustPct(r.data.proposed_pct); }
    catch (e) { setError(e?.response?.data?.detail || 'This link is invalid or expired.'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (action, adjusted_pct) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/syndication-offers/public/${token}/respond`, { action, adjusted_pct });
      setDone(r.data.status);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not submit your response.'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;

  const d = data.deal || {};
  const months = d.term_days ? Math.round(d.term_days / 30) : null;
  // 10/22: OPEN = the wizard's broadcast email — no proposed %, the syndicator names the share they want.
  const isOpen = data.offer_style === 'OPEN' || !(Number(data.proposed_pct) > 0);
  const alreadyAnswered = ['CONFIRMED', 'ADJUSTED', 'DECLINED', 'FINALIZED'].includes(data.status);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader><CardTitle>Syndication offer</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg bg-slate-100 p-3 text-sm space-y-1">
            <div className="font-medium">{d.name}</div>
            <div className="flex justify-between"><span className="text-slate-500">Funding</span><span>{formatCurrency(d.funding_amount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Factor</span><span>{d.factor}x</span></div>
            {months != null && <div className="flex justify-between"><span className="text-slate-500">Term</span><span>{months} mo</span></div>}
            <div className="flex justify-between"><span className="text-slate-500">Payback</span><span>{formatCurrency(d.payback)}</span></div>
          </div>

          {data.syndicator_name && <div className="text-sm text-slate-500 text-center">For {data.syndicator_name}</div>}
          {isOpen ? (
            <div className="text-center">
              <div className="text-sm text-slate-500">Available to syndicate</div>
              <div className="text-3xl font-semibold">{data.available_pct != null ? `${data.available_pct}%` : '—'}</div>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-sm text-slate-500">Your proposed allocation</div>
              <div className="text-3xl font-semibold">{data.proposed_pct}%</div>
            </div>
          )}

          {done || alreadyAnswered ? (
            <div className="text-center text-emerald-700 py-4">
              <CheckCircle className="w-8 h-8 mx-auto mb-2" />
              {(done === 'ADJUSTED' || (data.status === 'ADJUSTED' && (data.adjustment_status || 'pending') === 'pending')) ? (
                <p>Thank you — your request for <span className="font-medium">{done ? adjustPct : data.adjusted_pct}%</span> has been sent and is
                  awaiting approval{Number(data.proposed_pct) > 0 ? `; until then the proposed ${data.proposed_pct}% stands` : ''}.</p>
              ) : data.adjustment_status === 'approved' ? (
                <p>Your adjustment to <span className="font-medium">{data.adjusted_pct}%</span> was approved.</p>
              ) : data.adjustment_status === 'declined' ? (
                <p>Your adjustment to {data.adjusted_pct}% was declined — the proposed <span className="font-medium">{data.proposed_pct}%</span> stands.</p>
              ) : (
                <p>Thank you — your response (<span className="font-medium">{(done || data.status || '').toLowerCase()}</span>
                  {!done && data.final_pct > 0 ? ` · ${data.final_pct}%` : ''}) has been recorded.</p>
              )}
            </div>
          ) : isOpen && !adjusting ? (
            <div className="space-y-2">
              <label className="text-sm text-slate-600">What percentage would you like to take?</label>
              <Input type="number" min="0" max={data.available_pct || 100} step="0.5" placeholder="e.g. 25"
                value={adjustPct || ''} onChange={(e) => setAdjustPct(e.target.value)} />
              <div className="grid grid-cols-1 gap-2">
                <Button disabled={busy || !(Number(adjustPct) > 0)} onClick={() => respond('adjust', Number(adjustPct))}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Request {Number(adjustPct) > 0 ? `${adjustPct}%` : 'a share'}
                </Button>
                <Button variant="ghost" className="text-red-600" disabled={busy} onClick={() => respond('decline')}><XCircle className="w-4 h-4 mr-1" /> Decline</Button>
              </div>
            </div>
          ) : adjusting ? (
            <div className="space-y-2">
              <label className="text-sm text-slate-600">Enter the % you'd like to take</label>
              <Input type="number" value={adjustPct} onChange={(e) => setAdjustPct(e.target.value)} />
              <div className="flex gap-2">
                <Button className="flex-1" disabled={busy} onClick={() => respond('adjust', Number(adjustPct))}>Submit</Button>
                <Button variant="outline" onClick={() => setAdjusting(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <Button disabled={busy} onClick={() => respond('confirm')}><CheckCircle className="w-4 h-4 mr-1" /> Confirm {data.proposed_pct}%</Button>
              <Button variant="outline" disabled={busy} onClick={() => setAdjusting(true)}><Edit3 className="w-4 h-4 mr-1" /> Adjust amount</Button>
              <Button variant="ghost" className="text-red-600" disabled={busy} onClick={() => respond('decline')}><XCircle className="w-4 h-4 mr-1" /> Decline</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
